/**
 * @module filter.config
 *
 * single source of truth for the map filter panel. three registries —
 * `tabs`, `groups`, `filters` — drive everything downstream:
 *   - `FilterState` (filter.svelte.ts): one $state field per entry, from defaults
 *   - persistence walker (filter.controller.svelte.ts): `applyConfigToFilterState`
 *     reads / `buildPreferenceConfig` writes, both iterating `filters` by `id`
 *   - `FilterPanelRenderer.svelte`: iterates `groupsForTab(tab)` then `filters`
 *     per group, picking UI by `kind` and `allToggle`
 *   - `FilterSummaryStore` (filter.svelte.ts): tallies selected-of-total
 *
 * add a filter = append a `FilterDimension` here (+ a matching `FilterState`
 * field if its `id` is new — the registry declares the contract, it does not
 * create state).
 *
 * dimension fields:
 *   id         BE persistence key AND `filterState` field; dotted ids
 *              (`popVisibility.external`) address nested state; renaming breaks
 *              saved preferences
 *   tab/group  which tab, and a `GroupDescriptor.key` on that tab
 *   labelKey   i18n resolver (function form so it can flip per SaaS)
 *   persisted  whether save/load walks it (UI-only flags = false)
 *   kind       'boolean' (adds `defaultValue: () => boolean`) or 'multi' (adds
 *              `options: () => OptionDescriptor[]`)
 *   allToggle  multi only: 'none' | 'simple' (one "All") | 'split' ("All" + one
 *              toggle per `subsets` entry, e.g. lotStates "All active" /
 *              "All inactive"; 'split' needs `subsets` + `subsetLabelKey`)
 *   visible?   hide everywhere (renderer/persistence/summary skip); default visible
 *   readOnly?  visible but disabled; value forced from `defaultValue`;
 *              persistence + summary skip — for see-but-can't-toggle affordances
 *   affectsResults?  default true; `false` = display-only (e.g. `groupLots`
 *              clusters but hides nothing), excluded from the summary so the
 *              "active filters" badge stays accurate
 *   OptionDescriptor: value (BE key, lives under `state[id][value]`), labelKey,
 *              defaultOn? (default true)
 *
 * automatic behaviors:
 *   - renderer reads/writes `filterState` by `id` via the dotted-path walkers;
 *     `setAll` / `setSubset` go through `FilterState` helpers
 *   - persistence skips `!visible` / `readOnly` / `!persisted`; multi dims merge
 *     per-key on apply, so options added since last save keep their defaults
 *   - summary: boolean = 1-of-1, multi = selected-of-total; off booleans still
 *     count toward the visible-dimensions denominator
 *
 * recipes:
 *   - yes/no filter: `{ id, kind: 'boolean', tab, group, labelKey,
 *     persisted: true, defaultValue: () => false }` + a
 *     `$state(buildInitialBoolean(id))` field on `FilterState`
 *   - hide from SaaS: `visible: () => !isSaas()`
 *   - show-but-disabled for SaaS: `readOnly: () => isSaas()` +
 *     `defaultValue: () => isSaas()`
 *   - "All" toggle: `allToggle: 'simple'`; split toggles: `allToggle: 'split'`
 *     + `subsets` + `subsetLabelKey`
 *   - default-off option (lazy data): `{ value, labelKey, defaultOn: false }`
 *
 * cross-reference: `FilterProfileConfig` in `filter.controller.svelte.ts` is the
 * BE persistence shape this registry mirrors.
 */

import { LotState, LotType } from '$types/enums'
import { PopMaintenance } from '$mapapp/data/data.model'
import { availableTypes } from '$mapapp/stores/availableTypes.svelte'
import { jwt } from '$utils/jwt'
import { get } from 'svelte/store'
import {
	FilterId,
	GroupKey,
	LotStateSubset,
	type FilterDimension,
	type FilterTab,
	type GroupDescriptor
} from './filter.model'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * SaaS gate. Read synchronously via `get(jwt)` — this file is imported from
 * non-component contexts (controller, summary store) where the `$jwt` auto
 * subscription isn't available.
 */
const isSaas = () => !!get(jwt)?.saas

// internal constants — single source of truth for the lot-state and
// lot-type exclusion sets
const EXCLUDED_FILTER_STATES: LotState[] = [
	LotState.BIDDING_MAIL_SCHEDULED,
	LotState.BIDDING_MAIL_SENT
]
const SAAS_EXCLUDED_STATES: LotState[] = [
	LotState.UNVERIFIED,
	LotState.APPROVAL,
	LotState.OPTIMIZED,
	LotState.VERIFICATION,
	LotState.PARKED
]
const MM_EXCLUDED_STATES: LotState[] = [
	LotState.SUBMISSION
]
const MANUALLY_LOADED_FILTER_STATES: LotState[] = [LotState.COMPLETED, LotState.DISCARDED]
const SAAS_EXCLUDED_LOT_TYPES: LotType[] = [LotType.RETURN_FREIGHT]

/**
 * lot states visible to the current user: drops the always-excluded
 * mail-scheduling states and, for SaaS tenants, the MM-internal lifecycle
 * states. single source of truth for both the rendered options and the
 * `active` / `inactive` subset toggles, so a subset can never list a state
 * that isn't a visible option.
 */
const visibleLotStates = (): LotState[] =>
	Object.values(LotState).filter(
		(state) =>
			isNaN(Number(state)) &&
			!EXCLUDED_FILTER_STATES.includes(state) &&
			!(isSaas() && SAAS_EXCLUDED_STATES.includes(state)) &&
			!(!isSaas() && MM_EXCLUDED_STATES.includes(state))
	)

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

export const tabs: { id: FilterTab; labelKey: () => string }[] = [
	{ id: 'pop', labelKey: () => 'Map.filter.popPanel.title' },
	{ id: 'lot', labelKey: () => 'Map.filter.lotPanel.title' }
]

// ---------------------------------------------------------------------------
// Groups (visual sections, in render order per tab)
// ---------------------------------------------------------------------------

const groups: GroupDescriptor[] = [
	// pop tab
	{
		key: GroupKey.POP_CLASSIFICATION,
		tab: 'pop',
		labelKey: () => 'Map.popFilter.classification.title',
		// Pop visibility (internal/external) is a SaaS-only concept.
		visible: () => isSaas()
	},
	{ key: GroupKey.POP_STATE, tab: 'pop', labelKey: () => 'Map.popFilter.state.title' },
	{ key: GroupKey.POP_TYPE, tab: 'pop', labelKey: () => 'Map.popFilter.type.title' },
	// lot tab
	{ key: GroupKey.LOT_TYPE, tab: 'lot', labelKey: () => 'Map.lotFilter.type.title' },
	{ key: GroupKey.LOT_PROJECTS, tab: 'lot', labelKey: () => 'Map.lotFilter.projects.title' },
	{ key: GroupKey.LOT_STATE, tab: 'lot', labelKey: () => 'Map.lotFilter.state.title' },
	{ key: GroupKey.LOT_GROUPING, tab: 'lot', labelKey: () => 'Map.projectFilter.title' }
]

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export const filters: FilterDimension[] = [
	// ============================== POP TAB ==============================

	{
		id: FilterId.POP_VISIBILITY_EXTERNAL,
		kind: 'boolean',
		tab: 'pop',
		group: GroupKey.POP_CLASSIFICATION,
		labelKey: () => 'Map.filter.pop.visibility.extern',
		visible: () => isSaas(),
		persisted: true,
		defaultValue: () => true
	},
	{
		id: FilterId.POP_VISIBILITY_INTERNAL,
		kind: 'boolean',
		tab: 'pop',
		group: GroupKey.POP_CLASSIFICATION,
		labelKey: () => 'Map.filter.pop.visibility.intern',
		visible: () => isSaas(),
		persisted: true,
		defaultValue: () => true
	},
	{
		id: FilterId.POP_MAINTENANCE,
		kind: 'multi',
		tab: 'pop',
		group: GroupKey.POP_STATE,
		labelKey: () => 'Map.popFilter.state.title',
		persisted: true,
		allToggle: 'none',
		options: () =>
			Object.keys(PopMaintenance)
				.filter((k) => isNaN(Number(k)))
				.map((k) => ({
					value: k,
					labelKey: () => 'UI.pop.' + k.toLowerCase()
				}))
	},
	{
		id: FilterId.POP_TYPES,
		kind: 'multi',
		tab: 'pop',
		group: GroupKey.POP_TYPE,
		labelKey: () => 'Map.popFilter.type.title',
		persisted: true,
		allToggle: 'simple',
		// `defaultOn: false` because pop type keys are seeded lazily by
		// `getLocationTypes()` after the BE responds, and the existing seed
		// (see `availableTypes.svelte.ts`) initializes each new key to false
		// unless a saved preference already overrode it. `buildGeoJson.ts`
		// treats absence-or-false identically, so this mirrors current behavior.
		options: () =>
			availableTypes.locationTypes.map((lt) => ({
				value: lt.code,
				labelKey: () => lt.shortText,
				defaultOn: false
			}))
	},

	// ============================== LOT TAB ==============================

	{
		id: FilterId.LOT_TYPES,
		kind: 'multi',
		tab: 'lot',
		group: GroupKey.LOT_TYPE,
		labelKey: () => 'Map.lotFilter.type.title',
		persisted: true,
		allToggle: 'simple',
		// SaaS hides `RETURN_FREIGHT` — that lot type is MM-internal.
		options: () =>
			Object.values(LotType)
				.filter((k) => isNaN(Number(k)))
				.filter((k) => !(isSaas() && SAAS_EXCLUDED_LOT_TYPES.includes(k)))
				.map((k) => ({
					value: k,
					// `RETURN_FREIGHT` reuses a different i18n entry ("…new"); all
					// other LotType values map 1:1 to `UI.LotType.<KEY>`.
					// TODO JB: remove special handling? Clarify RETURN_FREIGHT wording
					labelKey: () => 'UI.LotType.' + (k === LotType.RETURN_FREIGHT ? 'RETURN_FREIGHT.new' : k)
				}))
	},
	{
		id: FilterId.SHOW_OWN_PROJECTS,
		kind: 'boolean',
		tab: 'lot',
		group: GroupKey.LOT_PROJECTS,
		// Label flips between "own projects" (SaaS) and "MM projects" (internal).
		labelKey: () => (isSaas() ? 'Map.lotFilter.ownProjects' : 'Map.lotFilter.mmProjects'),
		persisted: true,
		defaultValue: () => !isSaas()
	},
	{
		id: FilterId.SHOW_PUBLISHED,
		kind: 'boolean',
		tab: 'lot',
		group: GroupKey.LOT_PROJECTS,
		labelKey: () =>
			isSaas() ? 'LotPublishedState.publishedOnMm' : 'Map.lotFilter.publishedProjects',
		persisted: true,
		defaultValue: () => isSaas()
	},
	{
		id: FilterId.LOT_STATES,
		kind: 'multi',
		tab: 'lot',
		group: GroupKey.LOT_STATE,
		labelKey: () => 'Map.lotFilter.state.title',
		persisted: true,
		allToggle: 'split',
		options: () =>
			visibleLotStates().map((k) => ({
				value: k,
				labelKey: () => 'LotState.' + k,
				// COMPLETED / DISCARDED default OFF: those lots are not
				// shipped from the BE on initial load — they are fetched
				// on demand via `loadCompletedLotsOnce` / `loadDiscardedLotsOnce`
				// when the user opts in. Defaulting them on would cause an
				// empty layer until the lazy load resolves.
				// HOTFIX JB: checkboxes disabled for now.
				...(MANUALLY_LOADED_FILTER_STATES.includes(k)
					? { defaultOn: false, disabled: true }
					: {})
			})),
		// subsets are intersected with `visibleLotStates()` so a toggle never
		// references a state hidden from the current user — see setSubset in
		// filter.svelte.ts, which also guards against stray keys
		subsets: {
			[LotStateSubset.ACTIVE]: () =>
				['BIDDING', 'OPTIMIZED', 'HANDLING', 'ACCOUNTING', 'TARGET_ACTUAL_VERIFICATION'].filter(
					(state) => visibleLotStates().includes(state as LotState)
				),
			[LotStateSubset.INACTIVE]: () =>
				['UNVERIFIED', 'SUBMISSION', 'PARKED', 'APPROVAL', 'VERIFICATION'].filter((state) =>
					visibleLotStates().includes(state as LotState)
				)
		},
		subsetLabelKey: (k) =>
			k === LotStateSubset.ACTIVE ? 'Map.lotFilter.allActive' : 'Map.lotFilter.allInactive'
	},
	{
		id: FilterId.SHOW_REQUESTED_BY_MM,
		kind: 'boolean',
		tab: 'lot',
		group: GroupKey.LOT_STATE,
		labelKey: () => 'LotPublishedState.requestedByMm',
		// SaaS renders this as `checked disabled` — visible, always on, not
		// user-controllable. `readOnly` makes the renderer pass `disabled` and
		// force the displayed value from `defaultValue`; persistence skips it
		// (the BE never sees a stale `false` from a previous non-SaaS session).
		readOnly: () => isSaas(),
		persisted: true,
		defaultValue: () => isSaas()
	},
	{
		id: FilterId.GROUP_LOTS,
		kind: 'boolean',
		tab: 'lot',
		group: GroupKey.LOT_GROUPING,
		// Section header reuses `Map.projectFilter.title`; the checkbox itself
		// uses the "all" affordance key to match the pre-refactor wording.
		labelKey: () => 'Map.projectFilter.allCheckBox',
		persisted: true,
		// Display-only — clusters nearby lots into a single marker but does
		// not hide any. Excluded from `FilterSummary` so flipping it does
		// not increment "active filters" badges.
		affectsResults: false,
		defaultValue: () => false
	}
]

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

/** All groups for a tab, in registry order. */
export function groupsForTab(tab: FilterTab): GroupDescriptor[] {
	return groups.filter((g) => g.tab === tab)
}

/** Look up a dimension by its BE persistence id. */
export function getDimension(id: string): FilterDimension | undefined {
	return filters.find((f) => f.id === id)
}
